import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { InternalTableSchema, InternalColumnDef } from '../core/schema/types';
import { useColumnOrderPersistence, reconcileColumnOrder } from './useColumnOrderPersistence';
import { useColumnSizePersistence, reconcileColumnSizes } from './useColumnSizePersistence';
import { computePinOffsets, type PinOffsets } from '../core/engine/computePinOffsets';

interface UseTableColumnsParams {
  internalSchema: InternalTableSchema;
  effectiveSelectedColumnList: string[] | undefined;
  tableState: {
    columnOrder: string[];
    columnSizes: Record<string, number>;
  };
  stateActions: {
    setColumnOrder: (order: string[]) => void;
    setColumnSizes: (sizes: Record<string, number>) => void;
    resizeColumn: (field: string, width: number) => void;
  };
  showSelectionColumn: boolean;
}

interface UseTableColumnsReturn {
  visibleColumnsBase: ReadonlyArray<InternalColumnDef>;
  visibleColumns: ReadonlyArray<InternalColumnDef>;
  isReorderable: boolean;
  isResizable: boolean;
  handleColumnReorder: (sourceIndex: number, destIndex: number) => void;
  handleColumnResize: (field: string, width: number) => void;
  handleColumnResizeEnd: (field: string, width: number) => void;
  pinOffsets: PinOffsets;
}

export function useTableColumns({
  internalSchema,
  effectiveSelectedColumnList,
  tableState,
  stateActions,
  showSelectionColumn,
}: UseTableColumnsParams): UseTableColumnsReturn {
  const visibleColumnsBase = useMemo(() => {
    return internalSchema.columns.filter((col) => {
      if (col.visibility === 'hidden') return false;
      if (col.visibility === 'locked') return true;
      if (col.visibility === 'picker') {
        if (!effectiveSelectedColumnList || effectiveSelectedColumnList.length === 0) {
          return false;
        }
        return effectiveSelectedColumnList.includes(col.field);
      }
      return true;
    });
  }, [internalSchema.columns, effectiveSelectedColumnList]);

  const colPrefs = internalSchema.features.columnPreferences;
  const isReorderable = colPrefs?.reorderable === true;
  const { load: loadColumnOrder, save: saveColumnOrder } = useColumnOrderPersistence({
    enabled: isReorderable && colPrefs?.persistenceKey != null,
    tableKey: colPrefs?.persistenceKey ?? '',
  });

  const columnOrderInitRef = useRef(false);
  useEffect(() => {
    if (!isReorderable || columnOrderInitRef.current) return;
    columnOrderInitRef.current = true;
    const saved = loadColumnOrder();
    if (saved.length > 0) {
      const fields = visibleColumnsBase.map((c) => c.field);
      const reconciled = reconcileColumnOrder(saved, fields);
      stateActions.setColumnOrder(reconciled);
    }
  }, [isReorderable, loadColumnOrder, visibleColumnsBase, stateActions]);

  const visibleColumns = useMemo(() => {
    let cols = visibleColumnsBase;
    const order = tableState.columnOrder;
    if (isReorderable && order.length > 0) {
      const colMap = new Map(visibleColumnsBase.map((c) => [c.field, c]));
      const ordered: typeof visibleColumnsBase[number][] = [];
      for (const field of order) {
        const col = colMap.get(field);
        if (col) {
          ordered.push(col);
          colMap.delete(field);
        }
      }
      colMap.forEach((col) => ordered.push(col));
      cols = ordered;
    }
    const hasPinned = cols.some((c) => c.pinned != null);
    if (!hasPinned) return cols;
    const left = cols.filter((c) => c.pinned === 'left');
    const center = cols.filter((c) => c.pinned == null);
    const right = cols.filter((c) => c.pinned === 'right');
    return [...left, ...center, ...right];
  }, [visibleColumnsBase, tableState.columnOrder, isReorderable]);

  const handleColumnReorder = useCallback(
    (sourceIndex: number, destIndex: number) => {
      const displayOrder = visibleColumns.map((c) => c.field);
      const next = [...displayOrder];
      const [moved] = next.splice(sourceIndex, 1);
      next.splice(destIndex, 0, moved);
      stateActions.setColumnOrder(next);
      saveColumnOrder(next);
    },
    [visibleColumns, stateActions, saveColumnOrder],
  );

  const isResizable = colPrefs?.resizable === true;
  const { load: loadColumnSizes, save: saveColumnSizes } = useColumnSizePersistence({
    enabled: isResizable && colPrefs?.persistenceKey != null,
    tableKey: colPrefs?.persistenceKey ?? '',
  });

  const columnSizeInitRef = useRef(false);
  useEffect(() => {
    if (!isResizable || columnSizeInitRef.current) return;
    columnSizeInitRef.current = true;
    const saved = loadColumnSizes();
    if (Object.keys(saved).length > 0) {
      const fields = visibleColumnsBase.map((c) => c.field);
      const reconciled = reconcileColumnSizes(saved, fields);
      if (Object.keys(reconciled).length > 0) {
        stateActions.setColumnSizes(reconciled);
      }
    }
  }, [isResizable, loadColumnSizes, visibleColumnsBase, stateActions]);

  const handleColumnResize = useCallback(
    (field: string, width: number) => {
      stateActions.resizeColumn(field, width);
    },
    [stateActions],
  );

  const handleColumnResizeEnd = useCallback(
    (field: string, width: number) => {
      stateActions.resizeColumn(field, width);
      saveColumnSizes({ ...tableState.columnSizes, [field]: width });
    },
    [stateActions, saveColumnSizes, tableState.columnSizes],
  );

  const pinOffsets = useMemo<PinOffsets>(() => {
    return computePinOffsets(visibleColumns, tableState.columnSizes, showSelectionColumn);
  }, [visibleColumns, tableState.columnSizes, showSelectionColumn]);

  return {
    visibleColumnsBase,
    visibleColumns,
    isReorderable,
    isResizable,
    handleColumnReorder,
    handleColumnResize,
    handleColumnResizeEnd,
    pinOffsets,
  };
}
